<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ContactAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';
import {
  useContactsFilterState,
  fmtFilterDate,
} from './contactsFilterState';

const emit = defineEmits(['apply', 'reset']);
const store = useStore();
const route = useRoute();

// 归属者动态获取: 从agents store拉所有客服名, 新增客服自动出现
const agents = computed(() => store.getters['agents/getAgents'] || []);
const ownerOptions = computed(() => {
  const names = agents.value.map(a => a.name).filter(Boolean);
  return ['', ...names, '未分配'];
});
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

// 筛选状态存模块级单例: 搜索卸载/页面切换重挂后恢复(定制 2026-08-18, 根治自定义日期变回今天)
const {
  owner,
  inboxId,
  referer,
  visitorId,
  contactInfo,
  cardStatus,
  createdAfter,
  createdBefore,
  customDateRange,
  selectedRangeType,
  dedup,
  hasApplied,
} = useContactsFilterState();

// 名片状态选项(与ContactForm一致)
const cardStatusOptions = ['', '未识别已同步', '已查看无留联'];
// 留联状态选项: ''=全部, left=已留联, not_left=未留联
const contactInfoOptions = [
  { value: '', label: '全部' },
  { value: 'left', label: '已留联' },
  { value: 'not_left', label: '未留联' },
];

// 日期选择器(点开日历弹窗, 内含 今天/昨天/本月/上月 等快捷预设)
// 默认"今天"; 初值在 contactsFilterState 模块初始化(北京时区)

const buildFilters = () => ({
  owner: owner.value,
  inbox_id: inboxId.value,
  referer: referer.value,
  visitor_id: visitorId.value,
  contact_info: contactInfo.value,
  card_status: cardStatus.value,
  created_after: createdAfter.value ? `${createdAfter.value} 00:00:00` : '',
  created_before: createdBefore.value ? `${createdBefore.value} 23:59:59` : '',
  // 去重(定制 2026-09-22): 勾选后查询列表只显示非重复名片(后端排除黄色"重"=非首个,
  // 绿色首个与无标记保留); 同时导出接口也带上(后端先滤一遍, 前端去重兜底, 口径一致)
  dedup: dedup.value ? '1' : '',
});

const fmtDate = fmtFilterDate;

// 日历选择器选好范围后 → 填入 createdAfter/Before 并应用。
// 同时回写 v-model(customDateRange/selectedRangeType): 否则筛选条重挂载(搜索/切页回来)后
// 日历按钮显示过期的"今天", 与实际查询条件不一致(定制 2026-08-26)
const onDateRange = value => {
  const [start, end, rangeType] = value || [];
  createdAfter.value = start ? fmtDate(new Date(start)) : '';
  createdBefore.value = end ? fmtDate(new Date(end)) : '';
  if (start && end) customDateRange.value = [new Date(start), new Date(end)];
  if (rangeType) selectedRangeType.value = rangeType;
  hasApplied.value = true;
  emit('apply', buildFilters());
};

// 「查询」按钮: 记录已应用, 重挂载(搜索/切换页面回来)后据此恢复筛选
const onApply = () => {
  hasApplied.value = true;
  emit('apply', buildFilters());
};

// 重挂载恢复: 用过筛选(查询/选过日期)后, 筛选条因搜索或页面切换被卸载重挂时,
// 自动重新应用上次筛选(子组件 mounted 先于父组件, 父组件首屏 fetch 会带上筛选)。
// 未用过筛选(首次进入)不触发, 保持原有无筛选首屏。
onMounted(() => {
  if (hasApplied.value) {
    emit('apply', buildFilters());
  }
});

// 按当前筛选结果导出 CSV。dedup 开启时按手机号+邮箱去重(当月重复只留首条)。
// 保存走 showSaveFilePicker(弹"另存为"选路径), 不支持时回退直接下载。
const isExporting = ref(false);
const sourceLabels = {
  direct: '直接访问', baidu: '百度', bing: '必应', google: '谷歌',
  '360': '360搜索', sogou: '搜狗', zhihu: '知乎', douyin: '抖音',
  weibo: '微博', tencent: '腾讯', other: '其他',
};
const srcLabel = s => {
  if (!s) return '';
  if (sourceLabels[s]) return sourceLabels[s];
  if (s.startsWith('ad:')) return `广告:${s.slice(3)}`;
  return s;
};
// "另存为"文件句柄必须在用户点击手势窗口内(几秒)拿到;
// 若等分页拉完数据再弹, 手势已过期 → "Must be handling a user gesture"报错。
// 所以 onExport 一进来(还没任何 await)就先弹窗拿句柄, 数据拉完再写入。
const pickSaveHandle = async suggestedName => {
  if (!window.showSaveFilePicker) return null;
  try {
    // eslint-disable-next-line no-await-in-loop
    return await window.showSaveFilePicker({
      suggestedName,
      types: [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }],
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e; // 用户取消"另存为" → 整个导出中止
    return null; // NotAllowedError 等 → 回退直接下载
  }
};
const saveCsv = async (csv, suggestedName, handle) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  // File System Access API: 手势期间已拿到的句柄直接写入
  if (handle) {
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  // 回退: 直接下载到默认下载目录
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedName;
  a.click();
  URL.revokeObjectURL(url);
};
const onExport = async () => {
  if (isExporting.value) return;
  isExporting.value = true;
  // 点击手势窗口内先弹"另存为"拿句柄(之后的分页拉取会让手势失效)
  const suggestedName = `contacts${dedup.value ? '_dedup' : ''}.csv`;
  let handle = null;
  try {
    handle = await pickSaveHandle(suggestedName);
  } catch (e) {
    if (e.name === 'AbortError') {
      // 用户取消了另存为, 不导出
      isExporting.value = false;
      return;
    }
  }
  try {
    const filters = buildFilters();
    // 日期单例默认"今天", 但列表首屏(没点过查询/日历)是不带日期显示全部的。
    // 导出必须与列表口径一致: 用户没应用过筛选时不能把默认"今天"当条件发出去,
    // 否则列表几千条、导出只有当天一百多条(2026-09-01 导出太少事故)。
    if (!hasApplied.value) {
      filters.created_after = '';
      filters.created_before = '';
    }
    // 导出跟随当前视图, 与列表同源同接口: 对话(所有说话的)走 /contacts/active(后端只返回
    // 发过消息的), 留电视图走 /contacts(再由后端/列表口径呈现)。不要用一个接口+前端滤
    // has_messages 来模拟——两个接口的过滤口径在后端, 同源才不会漂移。
    const isChatView = route.name === 'contacts_dashboard_active';
    const all = [];
    let page = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      // 后端默认每页15条, 必须显式传 per_page=100(白名单值), 否则首轮15<25误判为末页只导出第一页
      const res = isChatView
        ? await ContactAPI.active(page, '-last_activity_at', 100, filters)
        : await ContactAPI.get(page, '-last_activity_at', '', filters, 100);
      const raw = res.data?.payload || [];
      all.push(...raw);
      if (raw.length < 100) break;
      page += 1;
      if (page > 200) break;
    }
    let out = all;
    if (dedup.value) {
      // 去重: 同一联系方式(手机/邮箱/微信/QQ/固话)只保留最早留电(contact_saved_at最早)的名片, 与列表"重"标记同口径。
      // 先按 contact_saved_at 升序(空值排最后)决定谁保留, 输出仍按原顺序(最近活跃在前)。
      const dedupKeys = c => {
        const ca = c.custom_attributes || {};
        return [
          (c.phone_number || '').trim(),
          (c.email || '').trim().toLowerCase(),
          (ca.wechat || '').trim(),
          (ca.qq || '').trim(),
          (ca.landline || '').trim(),
        ].filter(Boolean);
      };
      const savedAt = c => (c.custom_attributes || {}).contact_saved_at || '9999';
      const idx = all.map((c, i) => i).sort((a, b) => {
        const ta = savedAt(all[a]);
        const tb = savedAt(all[b]);
        return ta < tb ? -1 : ta > tb ? 1 : a - b;
      });
      const seen = new Set();
      const keep = new Set();
      idx.forEach(i => {
        const keys = dedupKeys(all[i]);
        // 任一联系方式已出现过 → 判重复丢弃; 无任何联系方式的名片不参与直接保留
        if (keys.length && keys.some(k => seen.has(k))) return;
        keys.forEach(k => seen.add(k));
        keep.add(i);
      });
      out = all.filter((c, i) => keep.has(i));
    }
    const headers = ['访客id', '电话', '邮箱', '来源', '归属者', '名片状态', '来源URL'];
    const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = out.map(c =>
      [
        c.name || '',
        c.phone_number || '',
        c.email || '',
        srcLabel((c.custom_attributes || {}).source),
        (c.custom_attributes || {}).owner || '',
        (c.custom_attributes || {}).card_status || '',
        (c.additional_attributes || {}).referer || '',
      ]
        .map(escape)
        .join(',')
    );
    const csv = `﻿${headers.join(',')}\n${rows.join('\n')}`;
    await saveCsv(csv, suggestedName, handle);
    useAlert(
      `导出完成：共 ${all.length} 条${dedup.value ? `，去重后 ${out.length} 条` : ''}`
    );
  } catch (error) {
    useAlert(error.message || '导出失败');
  } finally {
    isExporting.value = false;
  }
};

const reset = () => {
  owner.value = '';
  inboxId.value = '';
  referer.value = '';
  visitorId.value = '';
  contactInfo.value = '';
  cardStatus.value = '';
  createdAfter.value = '';
  createdBefore.value = '';
  dedup.value = false;
  customDateRange.value = undefined;
  selectedRangeType.value = undefined;
  hasApplied.value = false;
  emit('reset');
};
</script>

<template>
  <div
    class="flex flex-col gap-3 px-6 py-3 border-b border-n-slate-4 bg-n-surface-1"
  >
    <!-- 第一行: 客服/收件箱/来源页 -->
    <div class="flex flex-wrap items-end gap-x-4 gap-y-3">
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">{{
          $t('CONTACT_LIST_FILTER.OWNER')
        }}</span>
        <select
          v-model="owner"
          class="h-8 w-40 px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option value="">{{ $t('CONTACT_LIST_FILTER.ALL') }}</option>
          <option v-for="o in ownerOptions.slice(1)" :key="o" :value="o">{{ o }}</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">{{
          $t('CONTACT_LIST_FILTER.INBOX')
        }}</span>
        <select
          v-model="inboxId"
          class="h-8 w-40 px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option value="">{{ $t('CONTACT_LIST_FILTER.ALL') }}</option>
          <option v-for="i in inboxes" :key="i.id" :value="i.id">{{ i.name }}</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">留联状态</span>
        <select
          v-model="contactInfo"
          class="h-8 w-40 px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option v-for="o in contactInfoOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">名片状态</span>
        <select
          v-model="cardStatus"
          class="h-8 w-40 px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option value="">全部</option>
          <option v-for="s in cardStatusOptions.slice(1)" :key="s" :value="s">{{ s }}</option>
          <option value="__none__">未添加</option>
        </select>
      </div>

      <div class="flex flex-col gap-1 w-44">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">访客id</span>
        <input
          v-model="visitorId"
          type="text"
          placeholder="输入访客id"
          class="h-8 w-full px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
        />
      </div>

      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">{{
          $t('CONTACT_LIST_FILTER.REFERER')
        }}</span>
        <input
          v-model="referer"
          type="text"
          :placeholder="$t('CONTACT_LIST_FILTER.REFERER_PLACEHOLDER')"
          class="h-8 w-full px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
        />
      </div>
    </div>

    <!-- 第二行: 时间(单独一行, 左) + 操作按钮(右) -->
    <div
      class="flex items-center justify-between gap-4 pt-2 border-t border-n-slate-3 flex-wrap"
    >
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">{{
          $t('CONTACT_LIST_FILTER.CREATED_AT')
        }}</span>
        <div
          class="w-64 h-8 flex items-center [&>div]:!h-8 [&>div]:!w-full [&_button]:!h-8 [&_button]:!w-full [&_button]:!py-0 [&_button]:!rounded-md [&_button]:!border [&_button]:!border-n-slate-6 [&_button]:!bg-n-alpha-1 dark:[&_button]:!bg-n-solid-3"
        >
          <WootDatePicker
            v-model:date-range="customDateRange"
            v-model:range-type="selectedRangeType"
            @date-range-changed="onDateRange"
          />
        </div>
      </div>
      <div class="flex items-center gap-2">
        <label
          class="flex items-center gap-1 text-xs text-n-slate-11 whitespace-nowrap cursor-pointer"
        >
          <input
            v-model="dedup"
            type="checkbox"
            class="size-3.5 accent-n-brand cursor-pointer"
          />
          去重
        </label>
        <Button
          type="button"
          faded
          slate
          :label="isExporting ? '导出中...' : '导出'"
          :is-loading="isExporting"
          @click="onExport"
        />
        <Button
          type="button"
          :label="$t('CONTACT_LIST_FILTER.APPLY')"
          @click="onApply"
        />
        <Button
          type="button"
          faded
          slate
          :label="$t('CONTACT_LIST_FILTER.RESET')"
          @click="reset"
        />
      </div>
    </div>
  </div>
</template>
